import CustomDateFilter from "components/shared-components/RemoteTable/custom-date-filter";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import {
  CUSTOM_DATE_FILTER_TABLE_TYPE,
  CUSTOM_SELECT_FILTER_TABLE_TYPE,
} from "constants/RemoteDataTableContanst";
import {
  useCheckRole,
  useOrderStatus,
  useTimeSlots,
  useTypeOrder,
} from "hooks";
import { useListOrderCollector } from "hooks/order-collector/useOrderCollector";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { customFilter } from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import useCoordinateOrder from "./useCoordinateOrder";
import { actionAccess } from "constants/actionAccess";
import { SHIPPING_TYPE, STATUS } from "constants/order";

export const getInitOrderCollectorDetailParams = () => {
  return {
    sizePerPage: 10,
    page: -1,
    sort: "-checker_date",
    columnFilter: {},
    keywords: "",
    columnsVisible: {},
    fields: "",
    expand: "timeSlot",
  };
};

const orderStatusSelectedDefault = { value: "2", label: "Đã điều phối" };

const AllocateCollectorDetail = props => {
  const { isOpenModal, toggleModal, dataDetail, hubIdFilter } = props;
  const { t } = useTranslation(["allocateCollector"]);
  const navigate = useNavigate();
  const { isHubAdmin } = useCheckRole();

  /* ================================== START INIT STATE ==================================== */

  const [orderCollectorParams, setOrderCollectorParams] = useState({
    ...getInitOrderCollectorDetailParams(),
    expand: "timeSlot",
  });
  const [columnsTable, setColumnsTable] = useState([]);
  const [orderType, setOrderType] = useState([]);
  const [orderTypeSelect, setOrderTypeSelect] = useState(null);
  const [orderStatus, setOrderStatus] = useState([]);
  const [orderStatusSelected, setOrderStatusSelected] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlotsSelect, setTimeSlotsSelect] = useState(null);

  /* =================================== END INIT STATE ===================================== */

  /* ===================================== START HOOKS ====================================== */

  // ORDER COLLECTOR LIST

  const {
    data: orderCollectorList,
    refetch: fetchOrderCollectorList,
    isFetching: isLoadingOrderCollectorList,
  } = useListOrderCollector({
    collectorId: dataDetail.collectorId,
    params: orderCollectorParams,
  });

  // TYPE ORDER

  const onFetchOrderTypeSuccess = response => {
    setOrderType(response);
    fetchOrderStatus();
  };

  const { refetch: fetchOrderType, isFetching: isLoadingOrderType } =
    useTypeOrder(onFetchOrderTypeSuccess);

  // STATUS ORDER

  const onFetchOrderStatusSuccess = response => {
    setOrderStatus(response);
    fetchTimeSlots();
  };

  const { refetch: fetchOrderStatus, isFetching: isLoadingOrderStatus } =
    useOrderStatus(onFetchOrderStatusSuccess);

  // TIME SLOT

  const onFetchTimeSlots = response => {
    const list = response?.items || [];
    const result = [];

    list.forEach(item => {
      result.push({ label: item.name, value: item.id });
    });

    setTimeSlots(result);

    if (isOpenModal) {
      const date = new Date();
      const currentDate = moment(date).format("YYYY-MM-DD");

      setOrderCollectorParams(prev => ({
        ...prev,
        page: 1,
        sizePerPage: 10,
        run: true,
        columnFilter: {
          ...prev.columnFilter,
          "filter[status]": "2",
          "filter[action_date][lte]": currentDate,
        },
      }));
    }
  };

  const { refetch: fetchTimeSlots, isFetching: isLoadingTimeSlot } =
    useTimeSlots(
      {
        key: "order-collector",
        params: {
          "filter[type][in]": [0, 1],
          "filter[hub_id][in]": hubIdFilter,
        },
      },
      onFetchTimeSlots
    );

  useEffect(() => {
    if (isOpenModal) {
      setOrderCollectorParams(getInitOrderCollectorDetailParams);
      setOrderTypeSelect(null);
      setTimeSlotsSelect(null);
      setOrderStatusSelected(orderStatusSelectedDefault);

      fetchOrderType();

      initColumn();
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (isOpenModal && orderCollectorParams && orderCollectorParams.run) {
      fetchOrderCollectorList();
    }
  }, [orderCollectorParams, isOpenModal]);

  useEffect(() => {
    initColumn();
  }, [orderType, orderStatus, timeSlots, orderCollectorParams]);

  const onSuccesCoordinate = () => {
    fetchOrderCollectorList();
  };

  const { handleCoordinate, OrderCoordinateModal } = useCoordinateOrder({
    onSuccesCoordinate,
  });

  /* ===================================== END HOOKS ======================================== */

  /* ================================ START FUNCTION HANDLER ================================ */

  const initColumn = () => {
    if (
      orderCollectorParams.columnsVisible &&
      Object.keys(orderCollectorParams.columnsVisible).length > 0
    ) {
      initColumns.forEach(item => {
        if (orderCollectorParams.columnsVisible[item.dataField] !== undefined) {
          item.hidden = !orderCollectorParams.columnsVisible[item.dataField];
        }
      });
    }
    setColumnsTable(initColumns);
  };

  const filterOutItemObjectByKey = (obj, keyFilterOut) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([key, value]) => !key.startsWith(keyFilterOut)
      )
    );
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter }
  ) => {
    let columnFilterClone = {};
    let hasFilterActionDate = Object.keys(columnFilter).some(key =>
      key.startsWith("filter[action_date]")
    );
    if (hasFilterActionDate) {
      columnFilterClone = filterOutItemObjectByKey(
        orderCollectorParams.columnFilter,
        "filter[action_date]"
      );
    } else {
      const date = new Date();
      const currentDate = moment(date).format("YYYY-MM-DD");
      columnFilterClone = {
        ...filterOutItemObjectByKey(
          orderCollectorParams.columnFilter,
          "filter[action_date]"
        ),
        "filter[action_date][lte]": currentDate,
      };
    }

    setOrderCollectorParams(prev => ({
      ...prev,
      sort: sortField ? sortOrder + sortField : prev.sort,
      columnFilter: {
        ...columnFilterClone,
        ...columnFilter,
      },
      page: page || 1,
      sizePerPage: sizePerPage || 10,
    }));
  };

  const onSearchAllHandle = value => {
    setOrderCollectorParams(prev => ({
      ...prev,
      keywords: value,
    }));
  };

  const rowEventsHandler = {
    onDoubleClick: (_, row) => {
      onDoubleClickOrderDetail(row);
    },
  };

  const optionShowColumnHandler = (field, value) => {};

  const renderCustomer = row => {
    switch (row.type) {
      case 1:
        return <div>{row?.reciever_name}</div>;
      case 2:
        return <div>{row?.sender_name}</div>;
      case 3:
        return <div>{row?.sender_name}</div>;
      default:
        return <div></div>;
    }
  };

  const renderAddress = row => {
    switch (row.type) {
      case 1:
        return <div>{row?.reciever_full_address}</div>;
      case 2:
        return <div>{row?.sender_full_address}</div>;
      case 3:
        return <div>{row?.sender_full_address}</div>;
      default:
        return <div></div>;
    }
  };

  const renderStatus = row => {
    const order = orderStatus.find(order => order.value == row.status);
    return <span>{order?.label}</span>;
  };

  const renderType = row => {
    const order = orderType.find(order => order.value == row.type);
    return <span>{order?.label}</span>;
  };

  const formatDateTime = strDate => {
    try {
      return moment(strDate).format("DD/MM/YYYY");
    } catch (error) {
      return "";
    }
  };

  const onOpenOrderDetail = row => {
    window.open(`/manage-orders/${row.id}`, "_blank");
  };

  const onDoubleClickOrderDetail = row => {
    window.open(`/manage-orders/${row.id}`, "_blank", "noopener,noreferrer");
  };

  const onFilterOrderType = type => {
    const columnFilter = { ...orderCollectorParams.columnFilter };

    if (type) {
      columnFilter["filter[type]"] = type.value;
    } else {
      delete columnFilter["filter[type]"];
    }

    setOrderCollectorParams(prev => ({
      ...prev,
      columnFilter,
    }));
  };

  const onFilterOrderStatus = status => {
    const columnFilter = { ...orderCollectorParams.columnFilter };

    if (status) {
      columnFilter["filter[status]"] = status.value;
    } else {
      delete columnFilter["filter[status]"];
    }

    setOrderCollectorParams(prev => ({
      ...prev,
      columnFilter,
    }));
  };

  const onFilterOrderTimeSlot = timesLotId => {
    const columnFilter = { ...orderCollectorParams.columnFilter };

    if (timesLotId) {
      columnFilter["timeslot_id"] = timesLotId.value;
    } else {
      delete columnFilter["timeslot_id"];
    }

    setOrderCollectorParams(prev => ({
      ...prev,
      columnFilter,
    }));
  };

  /* ================================ END FUNCTION HANDLER ================================= */

  const listRowAction = [
    {
      icon: <i className="fas fa-eye fa-lg text-primary" />,
      text: t("table.order_detail"),
      onClick: onOpenOrderDetail,
    },
    !isHubAdmin && {
      actionAccess: actionAccess.GLOG_FUNCTION_ORDER_VIEW,
      icon: <i className="fas fa-location-arrow fa-lg text-success ms-2" />,
      text: t("actions.coordinate"),
      onClick: row => handleCoordinate(row),

      conditionVisible: {
        shipping_type: [SHIPPING_TYPE.COLLECTOR],
        status: [
          STATUS.COORDINATED,
          STATUS.HUB_ARRIVED,
          STATUS.NEW,
          STATUS.PENDING,
        ],
      },
    },
  ];

  const initColumns = [
    {
      dataField: "type",
      text: t("table.order_type"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select", {
                title: t("table.order_type").toLowerCase(),
              })}
              options={orderType}
              isLoading={isLoadingOrderType}
              classNamePrefix="select2-selection"
              value={orderTypeSelect}
              onChange={data => {
                onFilterOrderType(data);
                setOrderTypeSelect(data);
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return renderType(row);
      },
    },
    {
      dataField: "order_code",
      text: t("table.order_code"),
      sort: false,
      allowExport: true,
      align: "center",
      formatter: (cell, row) => (
        <Button color="link" onClick={() => onOpenOrderDetail(row)}>
          {cell}
        </Button>
      ),
    },
    {
      dataField: "status",
      text: t("table.order_status"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select", {
                title: t("table.order_status").toLowerCase(),
              })}
              options={orderStatus}
              isLoading={isLoadingOrderStatus}
              classNamePrefix="select2-selection"
              value={orderStatusSelected}
              onChange={data => {
                onFilterOrderStatus(data);
                setOrderStatusSelected(data);
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return renderStatus(row);
      },
    },
    {
      dataField: "contact_name",
      text: t("table.order_customer"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return <div style={{ minWidth: "250px" }}></div>;
      },
      formatter: (cell, row) => {
        return renderCustomer(row);
      },
    },
    {
      dataField: "full_address",
      text: t("table.full_address"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return <div style={{ minWidth: "250px" }}></div>;
      },
      formatter: (cell, row) => {
        return renderAddress(row);
      },
    },
    {
      dataField: "sale_name",
      text: t("table.sale_name"),
      sort: false,
      allowExport: true,
      align: "center",
    },
    {
      dataField: "action_date",
      text: t("table.action_date"),
      sort: false,
      align: "center",
      formatter: (cell, row) => {
        return row?.action_date ? formatDateTime(row?.action_date) : "";
      },
      filter: customFilter({
        type: CUSTOM_DATE_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <CustomDateFilter
            onFilter={onFilter}
            column={column}
            paramFilter={orderCollectorParams.columnFilter}
          />
        );
      },
      allowExport: true,
    },
    {
      dataField: "timeslot_id",
      text: t("table.order_time_slot"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select", {
                title: t("table.order_time_slot").toLowerCase(),
              })}
              options={timeSlots}
              isLoading={isLoadingTimeSlot}
              classNamePrefix="select2-selection"
              value={timeSlotsSelect}
              onChange={data => {
                onFilterOrderTimeSlot(data);
                setTimeSlotsSelect(data);
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return <span>{row?.timeSlot?.name}</span>;
      },
    },
  ];

  return (
    <React.Fragment>
      <Modal
        style={{ maxWidth: "95%" }}
        size="xl"
        isOpen={isOpenModal}
        role="dialog"
        autoFocus={true}
        centered={true}
        className="exampleModal"
        toggle={toggleModal}
      >
        <div className="modal-content">
          <ModalHeader toggle={toggleModal}>{t("title.detail")}</ModalHeader>
          <ModalBody>
            <RemoteDataTable
              keyField="id"
              data={orderCollectorList?.items || []}
              columns={columnsTable}
              page={orderCollectorParams.page}
              sizePerPage={orderCollectorParams.sizePerPage}
              totalSize={orderCollectorList?.totalCount || 0}
              onTableChange={handleTableChange}
              onSearchAllHandle={onSearchAllHandle}
              optionShowColumnHandel={optionShowColumnHandler}
              rowEvents={rowEventsHandler}
              listMultipleRowAction={[]}
              listRowAction={listRowAction}
              loading={isLoadingOrderCollectorList}
            ></RemoteDataTable>
          </ModalBody>
        </div>
      </Modal>
      {OrderCoordinateModal}
    </React.Fragment>
  );
};

AllocateCollectorDetail.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  dataDetail: PropTypes.object.isRequired,
};

AllocateCollectorDetail.defaultProps = {
  isOpenModal: false,
  dataDetail: {},
};

export default AllocateCollectorDetail;
